import type { CSSProperties } from 'react';

/**
 * Width of a column of a `table.table--fixed` in px, or `null` for a flexible column that shares
 * the width left by the fixed ones (text columns such as names). See `ColGroup` (shared/ui).
 */
export type ColumnWidth = number | null;

/** Average width of a character of 13px table text (Roboto, tabular figures). */
const CHAR = 7;
/** Horizontal padding of a cell (`.table th`, `.table td`: 12px each side). */
const CELL_PADDING = 24;
/** Extra outer padding of the first and the last column (24px instead of 12px). */
const EDGE_PADDING = 12;
/** The sort arrow of a header and its gap (`.th-sort::after`). */
const SORT_ARROW = 16;
/** Least width a flexible column keeps before the table scrolls horizontally. */
const FLEX_MIN = 160;

/** Estimated width in px of a text, or of a number of characters, in a table cell. */
export const textWidth = (text: string | number) =>
  Math.ceil((typeof text === 'string' ? text.length : text) * CHAR);

/**
 * Width of a column whose header reads `label` on one line (with the sort arrow) over cells whose
 * widest value is `content` px; an `edge` column (first or last) carries the wider outer padding.
 */
export function columnWidth(label: string, content: number, edge = false): number {
  const inner = Math.max(content, textWidth(label) + SORT_ARROW);
  return inner + CELL_PADDING + (edge ? EDGE_PADDING : 0);
}

/** Least width of a table with these columns: the fixed widths plus `flexMin` per flexible column. */
export const tableMinWidth = (widths: readonly ColumnWidth[], flexMin = FLEX_MIN) =>
  widths.reduce<number>((sum, width) => sum + (width ?? flexMin), 0);

/**
 * Style of a `table.table--fixed`: it scrolls inside `.table-wrap` rather than squeezing a column
 * below its width.
 */
export const fixedTableStyle = (widths: readonly ColumnWidth[]): CSSProperties => ({
  minWidth: tableMinWidth(widths),
});
